import React, { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { sendWelcomeEmail } from "../services/onboardingService";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=app.peak.peakandroid";
const APP_STORE_URL = "https://apps.apple.com/th/app/peak/id6784633966";

const DownloadPage: React.FC = () => {
  const { currentUser, userData, logout } = useAuth();
  const [params] = useSearchParams();
  const subscribed = params.get("subscription_success") === "true";
  const canceled = params.get("subscription_canceled") === "true";
  const existing = params.get("existing") === "1";
  const arrived =
    params.get("welcome") === "1" || subscribed || canceled;
  const welcomeStarted = useRef(false);

  useEffect(() => {
    if (!currentUser || existing || !arrived || welcomeStarted.current) return;
    welcomeStarted.current = true;
    sendWelcomeEmail(currentUser.email ?? "", userData?.name ?? "").catch((error) => {
      welcomeStarted.current = false;
      console.warn("[Download] welcome email failed (non-fatal):", error);
    });
  }, [arrived, currentUser, existing, userData?.name]);

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-base-200 px-6 py-12">
      <div className="w-full max-w-lg rounded-md border border-base-300 bg-base-100 px-6 py-8 shadow-sm">
        <p className="font-groteskLogo text-3xl">
          PEAK<span className="font-bold text-primary">^</span>
        </p>
        <h1 className="mt-6 font-heading text-4xl tracking-tight">Download the app</h1>
        <p className="mt-3 font-grotesk leading-relaxed text-base-content/80">
          {existing
            ? "You already have a PEAK account. Download the app and sign in there."
            : "Your account is ready. Training, wellness, and your plan live in the PEAK app."}
        </p>
        {subscribed && (
          <p className="mt-4 rounded-md bg-primary/10 px-4 py-3 text-sm font-grotesk">
            Your subscription is set up. Sign in with the same account in the app.
          </p>
        )}
        {canceled && (
          <p className="mt-4 rounded-md bg-base-200 px-4 py-3 text-sm font-grotesk">
            Checkout was canceled. You&apos;re on the free plan and can upgrade in the app.
          </p>
        )}
        <div className="mt-8 flex flex-col gap-2 sm:flex-row">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md bg-primary px-7 py-3.5 text-base font-semibold font-grotesk text-primary-content shadow-[0_6px_0_#005a7a] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_0_#005a7a] active:translate-y-1 active:shadow-[0_2px_0_#005a7a]"
          >
            Download on the App Store
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md bg-base-content px-7 py-3.5 text-base font-semibold font-grotesk text-base-100"
          >
            Get it on Google Play
          </a>
        </div>
        <div className="mt-8 flex items-center justify-between text-sm font-grotesk">
          <Link to="/" className="text-base-content/70 hover:text-base-content">
            Back to site
          </Link>
          {currentUser && (
            <button type="button" onClick={() => logout()} className="text-base-content/70 hover:text-base-content">
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
